import { create } from "zustand"

import type { StyleAdherence } from "@/features/studio-ai/types/studio-ai.types"

type StyleTemplateVisibility = "personal" | "company"

export type StyleTemplateDraftCandidate = {
  source: "local" | "server"
  updatedAt: number
  title: string
  description: string
  promptFragment: string
  brandColors: string[]
  visibility: StyleTemplateVisibility
}

export type StyleTemplatePendingAiUpdate = {
  title?: string
  description?: string
  prompt_fragment?: string
  brand_colors?: string[]
  media_type?: string
  createdAt: number
}

type StyleTemplateEditorState = {
  title: string
  description: string
  promptFragment: string
  brandColors: string[]
  visibility: StyleTemplateVisibility
  recommendedAdherence: StyleAdherence
  isDirty: boolean
  autosaveStatus: "idle" | "saving" | "saved" | "error"
  syncStatus: "idle" | "saving_local" | "saved_local" | "syncing" | "synced" | "sync_error"
  lastDraftSavedAt: number | null
  loadRevision: number
  pendingAiUpdate: StyleTemplatePendingAiUpdate | null
  restoreCandidate: StyleTemplateDraftCandidate | null

  openEditor: () => void
  clearEditor: () => void
  setTitle: (title: string) => void
  setDescription: (description: string) => void
  setPromptFragment: (promptFragment: string) => void
  setBrandColors: (brandColors: string[]) => void
  setVisibility: (visibility: StyleTemplateVisibility) => void
  setRecommendedAdherence: (adherence: StyleAdherence) => void
  setIsDirty: (isDirty: boolean) => void
  setAutosaveStatus: (status: "idle" | "saving" | "saved" | "error") => void
  setSyncStatus: (
    status: "idle" | "saving_local" | "saved_local" | "syncing" | "synced" | "sync_error"
  ) => void
  setLastDraftSavedAt: (timestamp: number | null) => void
  setRestoreCandidate: (candidate: StyleTemplateDraftCandidate | null) => void
  applyRestoreCandidate: () => void
  discardRestoreCandidate: () => void
  stageAiUpdate: (update: Omit<StyleTemplatePendingAiUpdate, "createdAt">) => void
  applyAiUpdate: () => void
  discardAiUpdate: () => void
}

const initialState = {
  title: "",
  description: "",
  promptFragment: "",
  brandColors: [],
  visibility: "personal" as StyleTemplateVisibility,
  recommendedAdherence: "inspired" as StyleAdherence,
  isDirty: false,
  autosaveStatus: "idle" as const,
  syncStatus: "idle" as const,
  lastDraftSavedAt: null,
  pendingAiUpdate: null as StyleTemplatePendingAiUpdate | null,
  restoreCandidate: null as StyleTemplateDraftCandidate | null,
}

function sanitizeBrandColors(colors: string[] | undefined): string[] {
  if (!colors?.length) return []
  return colors.map((color) => color.trim()).filter((color) => /^#[0-9a-fA-F]{6}$/.test(color))
}

export const useStyleTemplateEditorStore = create<StyleTemplateEditorState>((set, get) => ({
  ...initialState,
  loadRevision: 0,

  openEditor: () =>
    set((state) => ({
      ...initialState,
      loadRevision: state.loadRevision + 1,
    })),

  clearEditor: () =>
    set((state) => ({
      ...initialState,
      loadRevision: state.loadRevision,
    })),

  setTitle: (title) => set({ title, isDirty: true }),
  setDescription: (description) => set({ description, isDirty: true }),
  setPromptFragment: (promptFragment) => set({ promptFragment, isDirty: true }),
  setBrandColors: (brandColors) =>
    set({ brandColors: sanitizeBrandColors(brandColors), isDirty: true }),
  setVisibility: (visibility) => set({ visibility, isDirty: true }),
  setRecommendedAdherence: (recommendedAdherence) => set({ recommendedAdherence, isDirty: true }),
  setIsDirty: (isDirty) => set({ isDirty }),
  setAutosaveStatus: (autosaveStatus) => set({ autosaveStatus }),
  setSyncStatus: (syncStatus) => set({ syncStatus }),
  setLastDraftSavedAt: (lastDraftSavedAt) => set({ lastDraftSavedAt }),
  setRestoreCandidate: (restoreCandidate) => set({ restoreCandidate }),
  applyRestoreCandidate: () =>
    set((state) => {
      if (!state.restoreCandidate) return {}
      return {
        title: state.restoreCandidate.title,
        description: state.restoreCandidate.description,
        promptFragment: state.restoreCandidate.promptFragment,
        brandColors: sanitizeBrandColors(state.restoreCandidate.brandColors),
        visibility: state.restoreCandidate.visibility,
        restoreCandidate: null,
        isDirty: false,
        autosaveStatus: "saved",
        syncStatus: state.restoreCandidate.source === "server" ? "synced" : "saved_local",
        lastDraftSavedAt: state.restoreCandidate.updatedAt,
      }
    }),
  discardRestoreCandidate: () => set({ restoreCandidate: null }),

  stageAiUpdate: (update) =>
    set(() => {
      const nextUpdate: StyleTemplatePendingAiUpdate = {
        title: update.title?.trim() || undefined,
        description: update.description?.trim() || undefined,
        prompt_fragment: update.prompt_fragment?.trim() || undefined,
        brand_colors: sanitizeBrandColors(update.brand_colors),
        media_type: update.media_type,
        createdAt: Date.now(),
      }

      const hasMeaningfulUpdate = Boolean(
        nextUpdate.title ||
        nextUpdate.description ||
        nextUpdate.prompt_fragment ||
        (nextUpdate.brand_colors && nextUpdate.brand_colors.length > 0)
      )
      if (!hasMeaningfulUpdate) {
        return {}
      }

      return { pendingAiUpdate: nextUpdate }
    }),

  applyAiUpdate: () =>
    set((state) => {
      if (!state.pendingAiUpdate) return {}
      return {
        title: state.pendingAiUpdate.title ?? state.title,
        description: state.pendingAiUpdate.description ?? state.description,
        promptFragment: state.pendingAiUpdate.prompt_fragment ?? state.promptFragment,
        brandColors:
          state.pendingAiUpdate.brand_colors && state.pendingAiUpdate.brand_colors.length > 0
            ? state.pendingAiUpdate.brand_colors
            : state.brandColors,
        pendingAiUpdate: null,
        isDirty: true,
      }
    }),

  discardAiUpdate: () => {
    if (!get().pendingAiUpdate) return
    set({ pendingAiUpdate: null })
  },
}))
